// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import * as Sentry from "@sentry/react";
import { DocumentData } from "firebase/firestore";
import { action, computed, makeObservable } from "mobx";

import {
  compositeHydrationState,
  HydrationState,
  isHydrated,
} from "~hydration-utils";

import { WorkflowsTasksConfig } from "../../core/models/types";
import { SupervisionTaskCategory } from "../../core/WorkflowsTasks/fixtures";
import { TaskValidationError } from "../../errors";
import { SupervisionTaskUpdate } from "../../FirestoreStore";
import { RootStore } from "../../RootStore";
import { TENANT_CONFIGS } from "../../tenants";
import { Client } from "../Client";
import {
  CollectionDocumentSubscription,
  DocumentSubscription,
  ValidateFunction,
} from "../subscriptions";
import { SupervisionTaskUpdateSubscription } from "../subscriptions/SupervisionTaskUpdateSubscription";
import type { JusticeInvolvedPerson } from "../types";
import {
  SupervisionNeed,
  SupervisionTask,
  SupervisionTaskInterface,
  SupervisionTaskRecord,
  SupervisionTaskType,
} from "./types";

/**
 * Implements functionality shared by all Tasks, most notably the `Hydratable` interface.
 */
export abstract class TasksBase<
  PersonType extends Client,
  TaskRecord extends DocumentData,
  UpdateRecord extends SupervisionTaskUpdate,
> implements SupervisionTaskInterface
{
  rootStore: RootStore;

  person: Client;

  tasksConfiguration: WorkflowsTasksConfig;

  taskSubscription: DocumentSubscription<TaskRecord>;

  updatesSubscription: DocumentSubscription<UpdateRecord>;

  constructor(
    rootStore: RootStore,
    person: PersonType,
    tasksConfiguration: WorkflowsTasksConfig,
    validateRecord?: ValidateFunction<TaskRecord>,
  ) {
    makeObservable(this, {
      hydrate: action,
      hydrationState: computed,
      record: computed,
      updates: computed,
      tasks: true,
      needs: true,
    });
    this.person = person;
    this.rootStore = rootStore;
    this.tasksConfiguration = tasksConfiguration;

    // TODO(#7033): Change this to a query instead of a subscription
    this.taskSubscription = new CollectionDocumentSubscription<TaskRecord>(
      this.rootStore.firestoreStore,
      {
        key:
          person.stateCode === "US_ID"
            ? "usIdSupervisionTasksV2"
            : tasksConfiguration.collection,
      },
      person.recordId,
      undefined,
      validateRecord,
    );

    this.updatesSubscription =
      new SupervisionTaskUpdateSubscription<UpdateRecord>(
        this.rootStore.firestoreStore,
        person.recordId,
      );
  }

  get record(): TaskRecord | undefined {
    return this.taskSubscription.data;
  }

  get updates(): UpdateRecord | undefined {
    return this.updatesSubscription.data;
  }

  get tasks(): SupervisionTask<SupervisionTaskType>[] {
    if (!isHydrated(this)) return [];
    const { currentTenantId } = this.rootStore.tenantStore;
    if (!currentTenantId) return [];

    return (this.record?.tasks || []).flatMap(
      <T extends SupervisionTaskType>(
        task: SupervisionTaskRecord<T>,
      ): SupervisionTask<T>[] => {
        const TaskConstructor =
          TENANT_CONFIGS[currentTenantId].workflowsTasksConfig?.tasks[task.type]
            ?.constructor;

        if (TaskConstructor === undefined) {
          // TODO(#5622): Add a test to ensure a new name does not prevent
          // accessing defined tasks
          const error = new TaskValidationError(
            `Missing a class constructor for task with type: ${task.type}`,
          );
          Sentry.captureException(error, (scope) => {
            scope.setTag("currentTenantId", currentTenantId);
            return scope;
          });
          return [];
        }

        return [
          new TaskConstructor(
            this.rootStore,
            task,
            this.person,
            this.updates?.[task.type],
          ),
        ];
      },
    );
  }

  get needs(): SupervisionNeed[] {
    return this.record?.needs || [];
  }

  /**
   * Tasks that are not snoozed and are overdue
   */
  get overdueTasks(): SupervisionTask<SupervisionTaskType>[] {
    return this.readyTasks.filter((task) => task.isOverdue);
  }

  /**
   * Tasks that are not snoozed and are not overdue
   */
  get upcomingTasks(): SupervisionTask<SupervisionTaskType>[] {
    return this.readyTasks.filter((task) => !task.isOverdue);
  }

  /**
   * All tasks ordered by due date for the list of tasks on the preview panel and client profile
   */
  get orderedTasks(): SupervisionTask<SupervisionTaskType>[] {
    return this.tasks.sort(taskDueDateComparator);
  }

  /**
   * Ready tasks ordered by due date for the list of tasks displayed on the Task page.
   */
  get readyOrderedTasks(): SupervisionTask<SupervisionTaskType>[] {
    return this.readyTasks.sort(taskDueDateComparator);
  }

  /**
   * Tasks that are not snoozed
   */
  get readyTasks(): SupervisionTask<SupervisionTaskType>[] {
    return this.tasks.filter((task) => !task.isSnoozed);
  }

  get tasksConfig(): WorkflowsTasksConfig | undefined {
    if (!this.rootStore.currentTenantId) return;
    return TENANT_CONFIGS[this.rootStore.currentTenantId].workflowsTasksConfig;
  }

  hydrate(): void {
    this.taskSubscription.hydrate();
    this.updatesSubscription.hydrate();
  }

  get hydrationState(): HydrationState {
    return compositeHydrationState([
      this.taskSubscription,
      this.updatesSubscription,
    ]);
  }

  trackPreviewed(selectedCategory: SupervisionTaskCategory): void {
    this.rootStore.analyticsStore.trackTaskPreviewed({
      justiceInvolvedPersonId: this.person.pseudonymizedId,
      selectedCategory,
      taskTypes: [
        ...(this.tasks?.map((task) => task.type) ?? []),
        ...(this.needs?.map((need) => need.type) ?? []),
      ],
    });
  }
}

export const taskDueDateComparator = (
  a: { dueDate: Date },
  b: { dueDate: Date },
) => +a.dueDate - +b.dueDate;

/**
 * Orders people by their next (soonest) task due date, most overdue first.
 * People with no upcoming task sort last, since they have no urgency to convey.
 */
export function sortPeopleByNextTaskDueDate(
  personA: JusticeInvolvedPerson,
  personB: JusticeInvolvedPerson,
): number {
  const dueDateA = personA.supervisionTasks?.orderedTasks[0]?.dueDate;
  const dueDateB = personB.supervisionTasks?.orderedTasks[0]?.dueDate;

  if (!dueDateA && !dueDateB) return 0;
  if (!dueDateA) return 1;
  if (!dueDateB) return -1;

  return +dueDateA - +dueDateB;
}
